import { groupBy } from "lodash";
import { LD13Chart } from "store/slice/charts";
import { ChartWithCustomLine } from "./tenureConfig";

const useTenureLegend = (tenureChartData: LD13Chart) => {
    const users = tenureChartData.series.filter((p) => !p.overlapingId);
    const usersById = groupBy(users, "personId");
    const usersData = Object.values(usersById)
        .map((users) => users[0])
        .reverse();

    const sharedHistories = tenureChartData.series.filter((p) => p.overlapingId && p.overlapingId !== "");
    const sharedByOverlapingId = groupBy(sharedHistories, "overlapingId");
    const sharedHistoriesData = Object.entries(sharedByOverlapingId).map((data) => {
        const { color, companyName } = data[1][0];
        const names = data[1].map((person) => person.name);

        return {
            ...data[1][0],
            id: data[0],
            name: [...new Set(names)].join(" + "),
            color,
            companyName,
        };
    });

    const overlappingIds = tenureChartData?.series.map((s) => s.overlapingId);
    const userIds = tenureChartData?.series.map((s) => s.personId);

    const filteredUserData = usersData.filter((user) => userIds?.includes(user.personId));
    const filteredSharedHistories = sharedHistoriesData.filter((shared) =>
        overlappingIds?.includes(shared.overlapingId)
    );

    const exportWithLegend = (chart: ChartWithCustomLine | undefined, type: "image/png" | "image/svg+xml") => {
        if (!chart) return;

        // 1) Add the fake serie, rendered only when exporting the chart
        const fakeExportingLegend = {
            id: "exporting-legend",
            name: "Legend items",
            type: "column" as const,
            showInLegend: true,
            legendType: "point",
            enableMouseTracking: false,
            visible: true,
            data: [...filteredUserData, ...filteredSharedHistories].map((item, i) => ({
                ...item,
                color: chart.pointColors?.[i] || item.color,
                showInLegend: true,
                legendSymbolType: item.overlapingId ? "square" : "custom",
            })),
        };

        chart.addSeries(fakeExportingLegend, false);

        // 2) Enable legend for export and tweak the size
        chart.update(
            {
                legend: { enabled: true },
                exporting: {
                    chartOptions: {
                        chart: {
                            ...(chart.options.chart || {}),
                            spacing: [10, 10, 10, 10],
                            width: 1200,
                            height: Math.max(600, chart.chartHeight),
                        },
                    },
                },
            },
            false
        );
        chart.redraw();

        // 3) Export the chart
        chart.exportChartLocal({ type });

        // 4) Remove the serie (to avoid showing the chart legend in the browser), update the chart again
        const s = chart.get("exporting-legend");
        if (s) s.remove(false);

        chart.update(
            {
                legend: { enabled: false },
            },
            false
        );

        chart.redraw();
    };

    return {
        usersData,
        sharedHistoriesData,
        exportWithLegend,
    };
};

export default useTenureLegend;
